import { Dayjs } from 'dayjs';

/**
 * Shared positioning math for the scheduling lane grids (WeekGrid + DayGrid).
 * Pure functions only — no React, no DOM — so both grids can position blocks
 * and translate drag-drop pixel offsets identically without drifting apart.
 */

/** Minutes-since-midnight for a Dayjs instant (ignores the date part). */
export function minutesOfDay(d: Dayjs): number {
  return d.hour() * 60 + d.minute();
}

/** Pixel offset of `min` (minutes-since-midnight) within an openMin–closeMin track of height trackH. */
export function minutesToTop(min: number, openMin: number, closeMin: number, trackH: number): number {
  const clamped = Math.min(Math.max(min, openMin), closeMin);
  return ((clamped - openMin) / (closeMin - openMin)) * trackH;
}

/** Pixel height spanning startMin–endMin within an openMin–closeMin track, never below minHeight. */
export function minutesToHeight(
  startMin: number,
  endMin: number,
  openMin: number,
  closeMin: number,
  trackH: number,
  minHeight = 18,
): number {
  const s = Math.min(Math.max(startMin, openMin), closeMin);
  const e = Math.min(Math.max(endMin, openMin), closeMin);
  return Math.max(((e - s) / (closeMin - openMin)) * trackH, minHeight);
}

/**
 * Inverse of minutesToTop: a pixel offset within the track → minutes-since-midnight,
 * snapped to `snapMin` (default 15) and clamped to the track's open/close bounds.
 */
export function pointToSlot(
  offsetY: number,
  openMin: number,
  closeMin: number,
  trackH: number,
  snapMin = 15,
): number {
  const frac = Math.min(Math.max(offsetY / trackH, 0), 1);
  const raw = openMin + frac * (closeMin - openMin);
  return Math.round(raw / snapMin) * snapMin;
}

/**
 * Round a raw minutes-since-midnight value (e.g. an appointment's actual,
 * possibly off-grid start time) to the same `snapMin` resolution `pointToSlot`
 * produces from pixel math. Used to compare a drag's pixel-derived drop
 * position against an appointment's real origin start on equal footing — a
 * purely lateral drag (e.g. dropping in a different technician lane at
 * "the same" vertical spot) can shift the raw pixel offset by a few px even
 * when the user never touched the vertical axis, and comparing an unsnapped
 * origin against a snapped candidate would misreport that noise as a real
 * time change.
 */
export function snapMinutesTo(min: number, snapMin = 15): number {
  return Math.round(min / snapMin) * snapMin;
}
